import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../exo/ExoAvatar';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toIntlLocale } from '../../language/intlLocale';
import { MIN_SEARCH_LENGTH } from './useMessageSearch';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Split the text on the search term so each occurrence can be marked. The capture group
// keeps the separators, so nothing is lost.
const highlightTerm = (text, term, markSx) => {
  const content = String(text ?? '');
  if (!term) return content;

  const parts = content.split(new RegExp(`(${escapeRegExp(term)})`, 'gi'));
  const lowered = term.toLowerCase();

  return parts.map((part, index) =>
    part.toLowerCase() === lowered ? (
      <Box component="mark" key={index} sx={markSx}>
        {part}
      </Box>
    ) : (
      part
    )
  );
};

export const ChatMessageSearchResults = ({
  results,
  status,
  isLoadingMore,
  hasMore,
  loadMore,
  term,
  query,
  onSelect,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t, i18n } = useTranslation();
  const containerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // One formatter for the whole list — building an Intl formatter per row is expensive
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(toIntlLocale(i18n.language), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language]
  );

  const markSx = useMemo(
    () => ({
      backgroundColor: `${colors.info}40`,
      color: colors.text,
      borderRadius: '3px',
      padding: '0 2px',
    }),
    [colors.info, colors.text]
  );

  // Load the next page when the bottom of the results comes into view
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const root = containerRef.current;
    if (!sentinel || !root || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, results.length]);

  const centered = (content) => (
    <Box className="flex flex-col items-center justify-center h-full gap-2 px-6">
      {content}
    </Box>
  );

  if (status === 'idle') {
    return centered(
      <Typography
        variant="body2"
        sx={{ color: colors.text, opacity: 0.4, textAlign: 'center' }}
      >
        {t('Type at least {{min}} characters to search', {
          min: MIN_SEARCH_LENGTH,
        })}
      </Typography>
    );
  }

  if (status === 'searching' && results.length === 0) {
    return centered(<CircularProgress size={22} sx={{ color: colors.info }} />);
  }

  if (status === 'error') {
    return centered(
      <Typography
        variant="body2"
        sx={{ color: colors.error, textAlign: 'center' }}
      >
        {t('Search failed. Please try again.')}
      </Typography>
    );
  }

  if (results.length === 0) {
    return centered(
      <>
        <SearchOffIcon sx={{ fontSize: 40, color: colors.text, opacity: 0.2 }} />
        <Typography
          variant="body2"
          sx={{ color: colors.text, opacity: 0.4, textAlign: 'center' }}
        >
          {t('No messages found for')} “{query}”
        </Typography>
      </>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="flex flex-col h-full overflow-y-auto px-2 py-2 gap-1"
      sx={{
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.bgInput,
          borderRadius: '2px',
        },
      }}
    >
      {results.map((message) => (
        <Box
          key={message.id}
          // Tapping a result opens the conversation at that message
          role="button"
          tabIndex={0}
          onClick={() => onSelect?.(message)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect?.(message);
            }
          }}
          className="flex gap-3 p-3 rounded-xl"
          sx={{
            backgroundColor: colors.card,
            cursor: 'pointer',
            transition: 'background-color 120ms',
            '&:hover, &:focus-visible': { backgroundColor: colors.bgInput },
            '&:focus-visible': { outline: `2px solid ${colors.info}` },
          }}
        >
          <ExoAvatar
            size="32px"
            name={`${message.sender?.firstName || ''} ${message.sender?.lastName || ''}`}
            picture={
              message.sender?.picture?.temporaryUrl ||
              message.sender?.picture?.url
            }
          />
          <Box className="flex flex-col min-w-0 flex-1 gap-0.5">
            <Box className="flex items-baseline justify-between gap-2">
              <Typography
                variant="caption"
                fontWeight={600}
                noWrap
                sx={{ color: colors.text }}
              >
                {message.sender?.firstName} {message.sender?.lastName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text,
                  opacity: 0.45,
                  fontSize: '0.65rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {message.createdAt
                  ? dateFormatter.format(new Date(message.createdAt))
                  : ''}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.text,
                opacity: 0.85,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {highlightTerm(message.content, term, markSx)}
            </Typography>
          </Box>
        </Box>
      ))}

      {hasMore && <Box ref={loadMoreRef} sx={{ height: 1 }} aria-hidden="true" />}

      {isLoadingMore && (
        <Box className="flex justify-center py-3">
          <CircularProgress size={18} sx={{ color: colors.info }} />
        </Box>
      )}
    </Box>
  );
};
